import { h as __h } from "preact";
import { signal } from "@preact/signals";
/** @import { Signal } from "@preact/signals"; */
import { Component, createRef } from "preact";
/** @import { RefObject } from "preact"; */
import { renderSVG } from "uqr";
import { tfaRecoveryCode } from "~/backend/validate.js";
import { ApiError, apiRequest, authedApiRequest, response } from "~/lib/api.js";
import { identity } from "~/lib/identity.js";
import html from "~/util/html.js";
import { PromiseSignal } from "~/util/promise.js";
import { array, object, parse, string } from "~/util/validate.js";
import Banner from "~/component/Banner.js";
import Button from "~/component/Button.js";
import CopyBox from "~/component/CopyBox.js";
import Field from "~/component/Field.js";
import Icon from "~/component/Icon.js";
import Modal from "~/component/Modal.js";
import StepUp from "~/component/StepUp.js";
import input from "~/style/input.js";
import { adopt, importCss, CSS_MODULE_SCRIPTS } from "~/util/css.js";
const {
  default: sheet
} = CSS_MODULE_SCRIPTS ? await import("./Totp.module.css", {
  with: {
    type: "css"
  }
}) : await importCss("./Totp.module.css", import.meta.url);
const css = adopt(sheet);

/** @typedef {{ recovery_codes: string[]; reserved_code: string }} ConfirmedState */

/**
 * @extends {Component<EnrollTotpModalProps>}
 *
 * @typedef {object} EnrollTotpModalProps
 * @property {string} [uri]
 * @property {string} [token]
 * @property {string} [description]
 * @property {(tfa_code: string | undefined) => void} onClose
 */
export class EnrollTotpModal extends Component {
  /** @type {RefObject<Modal>} */
  #modalRef = createRef();

  /** @type {Signal<ConfirmedState | undefined>} */
  #confirmed = signal();
  #acknowledged = signal(false);
  render() {
    const confirmed = this.#confirmed.value;
    const acknowledged = this.#acknowledged.value;
    return __h(Modal, {
      ref: this.#modalRef,
      title: confirmed ? "Download your backup codes" : "Set up your authenticator app",
      open: Boolean(this.props.uri),
      closable: !confirmed || acknowledged,
      onClose: () => {
        this.props.onClose(confirmed?.reserved_code);
        this.#confirmed.value = undefined;
        this.#acknowledged.value = false;
      }
    }, this.props.description && __h("p", {}, this.props.description), confirmed ? __h(RecoveryCodes, {
      codes: confirmed.recovery_codes,
      acknowledged: acknowledged,
      onAcknowledgeChange: (/** @type {boolean} */value) => {
        this.#acknowledged.value = value;
      }
    }) : __h(ConfirmTotp, {
      uri: this.props.uri ?? "",
      token: this.props.token,
      onConfirm: (/** @type {ConfirmedState} */confirmed) => {
        this.#confirmed.value = confirmed;
      }
    }));
  }
}

/**
 * @extends {Component<ConfirmTotpProps>}
 *
 * @typedef {object} ConfirmTotpProps
 * @property {string} uri
 * @property {string} [token]
 * @property {(confirmed: ConfirmedState) => void} onConfirm
 */
class ConfirmTotp extends Component {
  #stepUpRef = (/** @type {{ current: StepUp }} */createRef());
  get #qr() {
    try {
      const secret = new URL(this.props.uri).searchParams.get("secret");
      if (!secret) return undefined;
      const svg = renderSVG(this.props.uri, {
        pixelSize: 2,
        whiteColor: "transparent",
        blackColor: "currentColor"
      });
      return {
        svg,
        secret
      };
    } catch {
      return undefined;
    }
  }
  #confirmSetup = new PromiseSignal(/** @param {SubmitEvent & { currentTarget: HTMLFormElement }} ev */
  async ev => {
    ev.preventDefault();
    const form = new FormData(ev.currentTarget);
    const {
      tfa_code
    } = parse(object({
      tfa_code: string
    }), Object.fromEntries(form.entries()));
    const res = this.props.token ? await apiRequest("POST", "/v2/tfa/setup/confirm", {
      json: {
        tfa_code,
        token: this.props.token
      },
      schema: response(array(tfaRecoveryCode))
    }) : await authedApiRequest("POST", "/v2/tfa/confirm", {
      json: {
        tfa_code,
        token: await this.#stepUpRef.current.requestToken("tfa_confirm")
      },
      schema: response(array(tfaRecoveryCode))
    });
    const [reserved_code, ...recovery_codes] = res.data.map(c => c.code);
    this.props.onConfirm({
      recovery_codes,
      reserved_code: (/** @type {string} */reserved_code)
    });
  });
  render() {
    const confirmError = this.#confirmSetup.error && TotpError.from(this.#confirmSetup.error);
    const qr = this.#qr;
    if (!qr) {
      return __h("p", {
        "class": css.error
      }, "There was an error generating your two-factor authentication code.");
    }
    return __h("form", {
      "class": css.form,
      onSubmit: (/** @type {SubmitEvent & { currentTarget: HTMLFormElement }} */ev) => {
        if (ev.submitter?.getAttribute("formmethod") === "dialog") return;
        this.#confirmSetup.invoke(ev);
      },
      onReset: this.#confirmSetup.reset
    }, __h("ol", {
      "class": css.steps
    }, __h("li", {}, __h("p", {}, "Scan this code with an authenticator app such as", " ", __h("a", {
      href: "https://1password.com",
      target: "_blank",
      rel: "noopener noreferrer"
    }, "1Password"), " ", "or", " ", __h("a", {
      href: "https://authy.com",
      target: "_blank",
      rel: "noopener noreferrer"
    }, "Authy"), "."), __h("div", {
      "class": css.scan
    }, __h("div", {
      "class": css.qr,
      dangerouslySetInnerHTML: {
        __html: qr.svg
      }
    }), __h("div", {
      "class": css.manual
    }, __h("p", {
      "class": css.blurb
    }, "If the QR code doesn't work, enter this secret manually instead."), __h(CopyBox, {
      value: qr.secret
    })))), __h("li", {}, __h("p", {}, "Enter the code it generates to enable two-factor authentication."), __h("div", {
      "class": css.inputs
    }, __h(Field, {
      "for": "tfa_code",
      label: "Authenticator code",
      validity: {
        patternMismatch: "Authenticator codes are always six numbers."
      }
    }, __h("input", {
      "class": input.input,
      id: "tfa_code",
      type: "text",
      name: "tfa_code",
      inputmode: "numeric",
      autocomplete: "one-time-code",
      pattern: "[0-9]{6}",
      maxlength: 6,
      required: true
    })), !this.props.token && __h(StepUp, {
      ref: this.#stepUpRef,
      error: confirmError
    }), __h("p", {
      "class": css.error
    }, confirmError?.message)))), __h("div", {
      "class": css.actions
    }, __h(Button, {
      level: "primary",
      type: "submit",
      loading: this.#confirmSetup.loading
    }, "Enable 2FA"), __h(Button, {
      type: "submit",
      formmethod: "dialog",
      formnovalidate: true,
      disabled: this.#confirmSetup.loading
    }, "Cancel")));
  }
}

/**
 * @extends {Component<RecoveryCodesProps>}
 *
 * @typedef {object} RecoveryCodesProps
 * @property {string[]} codes
 * @property {boolean} acknowledged
 * @property {(value: boolean) => void} onAcknowledgeChange
 */
class RecoveryCodes extends Component {
  #beforeUnload = (/** @type {BeforeUnloadEvent} */e) => {
    if (!this.props.acknowledged) e.preventDefault();
  };

  /** @override */
  componentDidMount() {
    window.addEventListener("beforeunload", this.#beforeUnload);
  }

  /** @override */
  componentWillUnmount() {
    window.removeEventListener("beforeunload", this.#beforeUnload);
  }
  #downloadCodes = () => {
    const blob = new Blob([this.props.codes.join("\n") + "\n"], {
      type: "text/plain"
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "phaze-recovery-codes.txt";
    a.click();

    // wait until the next tick to revoke the object URL in case downloading is async
    setTimeout(() => URL.revokeObjectURL(url), 0);
  };
  render() {
    const {
      codes,
      acknowledged
    } = this.props;
    return [__h("p", {}, "These backup codes let you log into Phaze if you lose access to your authenticator app. Keep\n\t\t\t\tthem in a safe place!"), __h("p", {}, __h("strong", {}, "If you lose both your authenticator app and backup codes, we cannot help you recover your\n\t\t\t\t\taccount.")), __h(Banner, {
      icon: __h(Icon, {
        name: "warning"
      }),
      kind: "warning"
    }, "Once you close this dialog, you won't be able to see your backup codes again."), __h("ul", {
      "class": css.codes
    }, codes.map(code => __h("li", {}, __h("code", {}, code)))), __h("label", {
      "class": css.confirm
    }, __h("input", {
      type: "checkbox",
      checked: acknowledged,
      onChange: (/** @type {Event & { currentTarget: HTMLInputElement }} */e) => {
        this.props.onAcknowledgeChange(e.currentTarget.checked);
      }
    }), __h("span", {}, "I've saved the codes in a safe place")), __h("form", {
      "class": css.actions
    }, __h(Button, {
      level: "primary",
      onClick: this.#downloadCodes
    }, "Download codes"), __h(Button, {
      disabled: !acknowledged,
      type: "submit",
      formmethod: "dialog",
      formnovalidate: true
    }, "Done"))];
  }
}

/**
 * @typedef {object} DisableTotpModalProps
 * @property {RefObject<Modal>} modalRef
 */

/** @extends {Component<DisableTotpModalProps>} */
export class DisableTotpModal extends Component {
  #stepUpRef = (/** @type {{ current: StepUp }} */createRef());
  #disable = new PromiseSignal(/** @param {SubmitEvent & { currentTarget: HTMLFormElement }} ev */
  async ev => {
    ev.preventDefault();
    await authedApiRequest("DELETE", "/v2/tfa", {
      json: {
        token: await this.#stepUpRef.current.requestToken("tfa_delete")
      }
    });
    this.props.modalRef.current?.close();
    identity.fetch();
  });
  render() {
    const {
      modalRef
    } = this.props;
    const error = this.#disable.error && TotpError.from(this.#disable.error);
    return __h(Modal, {
      ref: modalRef,
      title: "Disable two-factor authentication"
    }, __h("p", {}, "Verify your identity to disable two-factor authentication. Your account will only be\n\t\t\t\t\tprotected by your password."), __h("form", {
      "class": css.form,
      onSubmit: (/** @type {SubmitEvent & { currentTarget: HTMLFormElement }} */ev) => {
        if (ev.submitter?.getAttribute("formmethod") === "dialog") return;
        this.#disable.invoke(ev);
      },
      onReset: this.#disable.reset
    }, __h(StepUp, {
      ref: this.#stepUpRef,
      error: error
    }), __h("p", {
      "class": css.error
    }, error?.message), __h("div", {
      "class": css.actions
    }, __h(Button, {
      level: "primary",
      kind: "negative",
      type: "submit",
      loading: this.#disable.loading
    }, "Disable 2FA"), __h(Button, {
      type: "submit",
      formmethod: "dialog",
      formnovalidate: true,
      disabled: this.#disable.loading
    }, "Cancel"))));
  }
}
class TotpError extends ApiError {
  /** @override */
  get messages() {
    return {
      [ApiError.noField]: {
        invalid_credentials: "Your password is incorrect.",
        invalid_token: "Your verification has expired. Please try again.",
        tfa_required: "A two-factor authentication code is required.",
        bad_request: "The code you entered is invalid.",
        conflict: "Two-factor authentication is already enabled.",
        not_found: "Setup has expired. Please try again."
      }
    };
  }
}